import { AxiosError } from "axios";

import { getUserInternalAccessToken } from "@/api/access-token-backoffice";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IGlobalVariable } from "@/interfaces/global-variable.interfaces";
import IWorkflowElement from "@/interfaces/workflow-element.interfaces";
import IWorkflowEngine from "@/interfaces/workflow-engine.interfaces";
import { apiBackofficeT2b } from "@/lib/axios";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

const baseWorkflowEngineUrl = "v1/workflowengine";
const baseWorkflowElementUrl = "v1/workflowelement";
const baseGlobalVariableUrl = "v1/workflowglobalvariable";

export async function getWorkflowEngine(
  pagination: RequestPagination
): Promise<PaginatedResponse<IWorkflowEngine>> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const paginationQuery = buildPaginationQuery(pagination);
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    const response = await apiBackofficeT2b.get(
      `${baseWorkflowEngineUrl}?${paginationQuery}`,
      config
    );
    return response.data as PaginatedResponse<IWorkflowEngine>;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch workflow engine");
    }
    throw new Error("Failed to fetch workflow engine");
  }
}

export async function getWorkflowEngineById(workflowId: number): Promise<IWorkflowEngine> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    const response = await apiBackofficeT2b.get(`${baseWorkflowEngineUrl}/${workflowId}`, config);
    return response.data as IWorkflowEngine;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch workflow engine by id");
    }
    throw new Error("Failed to fetch workflow engine by id");
  }
}

export async function getWorkflowElements(
  pagination: RequestPagination
): Promise<PaginatedResponse<IWorkflowElement>> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const paginationQuery = buildPaginationQuery(pagination);
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    const response = await apiBackofficeT2b.get(
      `${baseWorkflowElementUrl}?${paginationQuery}`,
      config
    );
    return response.data as PaginatedResponse<IWorkflowElement>;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch workflow elements");
    }
    throw new Error("Failed to fetch workflow elements");
  }
}

export async function getWorkflowElementById(id: number): Promise<IWorkflowElement> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    const response = await apiBackofficeT2b.get(`${baseWorkflowElementUrl}/${id}`, config);
    return response.data as IWorkflowElement;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch workflow element by id");
    }
    throw new Error("Failed to fetch workflow element by id");
  }
}

export async function getGlobalVariables(): Promise<PaginatedResponse<IGlobalVariable>> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    const response = await apiBackofficeT2b.get(`${baseGlobalVariableUrl}`, config);
    return response.data as PaginatedResponse<IGlobalVariable>;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch global variables");
    }
    throw new Error("Failed to fetch global variables");
  }
}
